import { MembershipFragment, Membership_State } from '@occc/graphql';
import {
  useMembershipStateTransition,
  useMembershipDelete,
  useMembershipScrubData,
} from '@occc-app/dataAccessLayer/membership/mutations';
import { snackbar } from '@occc-app/design-system';
import { navigateTo } from '@occc-app/lib/router';
import { routeCreator } from '@occc-app/routes/Member/utils';

import MemberStateMenu from './MemberStateMenu';

export type MemberStateMenuContainerProps = {
  member: MembershipFragment;
  isUserPermittedToUpdateMemberState: boolean;
  refetchMembership: () => void;
};

const onCompleted = () => {
  return snackbar.success('The user was successfully deleted.');
};

export const MemberStateMenuContainer = ({
  member,
  isUserPermittedToUpdateMemberState,
  refetchMembership,
}: MemberStateMenuContainerProps) => {
  const [membershipStateTransition, { loading: isMembershipStateTransitionInProgress }] =
    useMembershipStateTransition();
  const [deleteAccount, { loading: isDeleteAccountInProgress }] = useMembershipDelete();
  const [scrubData, { loading: isLoadingScrubDataInProgress }] = useMembershipScrubData();

  const id = member.id;
  const customerUuid = member.customerUuid;
  const isDeletionInProgress =
    isMembershipStateTransitionInProgress ||
    isDeleteAccountInProgress ||
    isLoadingScrubDataInProgress;

  const onMemberStateChange = async (state: Membership_State) => {
    await membershipStateTransition({
      variables: {
        input: { id, state },
      },
      onCompleted: () => {
        snackbar.success('Membership state has been updated successfully.');

        // If the member was deleted, navigate back to the members list
        if (state === Membership_State.Deleted) {
          navigateTo(routeCreator.base());
        }
      },
      onError: () => snackbar.error('Something went wrong. Please try again.'),
    });
  };

  const onDelete = () =>
    deleteAccount({
      variables: { input: { membershipUuid: id } },
      onCompleted,
    }).finally(refetchMembership);

  const onScrubData = () =>
    scrubData({
      variables: { input: { customerUuid: customerUuid! } },
      onCompleted,
    }).finally(refetchMembership);

  return (
    <MemberStateMenu
      selectedState={member.state}
      memberPhone={member.phone || undefined}
      customerUuid={customerUuid}
      memberState={member.state}
      isDeletionInProgress={isDeletionInProgress}
      isUserPermittedToUpdateMemberState={isUserPermittedToUpdateMemberState}
      onMemberStateChange={onMemberStateChange}
      onMemberDelete={onDelete}
      onMemberScrubData={onScrubData}
    />
  );
};
